<script>
import Loading from "../../common/loading.vue";
import CalendarClass from "./calendar/Calendar.js";
import Calendar from "./calendar/calendar.vue"
import {
    addOneHour,
    getCurrentEvents,
    getDateInformation,
    getElementsBeforeDate, getExpiryMessage,
    getRelativeDateString,
    parseBusinessHours
} from "./calendar/calendarUtils";
import PublishCalendarActionbar from "./publish-calendar-actionbar.vue";
import API from "../../../API";
import {constructPublishData} from "./publishUtils";
import toaster from "../../common/toaster";
import {transformAPIResponseIntoCapitalizedJSON, transformCapitalizedJSONIntoSnakeCase} from "../../../utility";
import CalendarEvent from "./calendar/event/calendar-event.vue";
import {getLayerIdLocalStorageKey, getPublishFilterLocalStorageKey} from "../../../../utils/publish";
import transmission from "./transmission.vue";
import PublishFooter from "./publish-footer.vue";
import CalendarSettingsDialog from "./calendar/settings/calendar-settings-dialog.vue";
import CalendarPrivileges from "./calendar/CalendarPrivileges";
import ApprovalSystem from "./ApprovalSystem";

/**
     * @typedef {Object} CalendarChangeEvent
     * @property {EventImpl} event
     * @property {EventImpl} oldEvent
     * @property {Array} relatedEvents
     * @property {Function} revert
     */

    /**
     * @typedef {Object} CalendarDropEvent
     * @property {Boolean} allDay
     * @property {DateTime} date
     * @property {string} dateStr
     * @property {Object} draggedEl
     * @property {MouseEvent} jsEvent
     * @property {ViewImpl} view
     */

    export default {
        name: "publish-calendar",
        components: {CalendarSettingsDialog, PublishFooter, transmission, CalendarEvent, PublishCalendarActionbar, Loading, Calendar},
        props: {
            layers: Array,
            /** @type {ScreensData} */
            screensData: Object,
            approvePrivilege: String,
            deletePrivilege: String,
            editPrivilege: String,
            /** @type {CalendarSettings} */
            calendarProjectSettings: Object,
            publishingDisabled: Boolean,
            value: Number
        },
        data() {
            return {
                /** @type {CalendarClass} */
                calendar: undefined,
                /** @type {CalendarPublishLayer} */
                activeLayer: undefined,
                activeEventId: undefined,
                loading: true,
                showSettings: false,
                businessHours: undefined,
            }
        },
        computed: {
            privileges() {
                return new CalendarPrivileges(this.deletePrivilege, this.editPrivilege, this.approvePrivilege);
            },
            approvalSystem() {
                return new ApprovalSystem({
                    approvePrivilege: this.approvePrivilege,
                    onElementUpdate: async (index) => await this.updateEvent(this.activeLayer.elements[index], false),
                    getElements: () => this.activeLayer.elements,
                });
            },
            currentEvents() {
                return getCurrentEvents(this.activeLayer);
            },
            translations() {
                return {
                    published: Lang.langGet("The layer will be published in a few moments."),
                    outdated: Lang.langGet("Layer elements outdated. Please reload the layer."),
                    publishDialogText: Lang.langGet('You will publish the whole calendar to '),
                    noApprove: Lang.langGet("You don't have the right to approve.")
                };
            },
            publishDialogText() {
                const screenName = this.screensData.all_screens.find(screen => screen.value === this.calendar.screen)?.text;
                return `${this.translations.publishDialogText} "${screenName}"`;
            },
            publishDialogExpiryWarningText() {
                return getExpiryMessage(Laravel.calendarExpiry);
            },
            deletedElements() {
                if (!this.activeLayer?.elements) return [];
                return this.activeLayer.elements.filter(element => element.deletedAt);
            },
        },
        methods: {
            async handleApproveAll() {
                this.loading = true;
                await this.approvalSystem.approveAll();
                await this.reload();
                this.loading = false;
            },
            async handleDisapproveAll() {
                this.loading = true;
                await this.approvalSystem.disapproveAll();
                await this.reload();
                this.loading = false;
            },
            /** @param layer {CalendarPublishLayer} */
            setActiveLayer(layer) {
                if (!layer) this.activeLayer = undefined;

                layer.name = layer.name.replaceAmpGtLt();
                layer.fullname = layer.fullname.replaceAmpGtLt();
                layer.fullnameSize = layer.fullnameSize.replaceAmpGtLt();
                this.activeLayer = layer;
            },
            /** @param layer {CalendarPublishLayer} */
            updateLayers(layer) {
                const newLayers = [...this.layers];
                const targetLayer = newLayers.find(l => l.id === layer.id);
                if (targetLayer) {
                    if (!layer.elements.length) {
                        targetLayer.state = 'empty';
                    }
                    else if (layer.elements.some(el => el.state === 'tobesynced')) {
                        targetLayer.state = 'tobesynced';
                    }
                    else {
                        targetLayer.state = 'synced';
                    }
                }
                this.$emit('update:layers', newLayers);
            },
            async loadActiveLayer() {
                this.loading = true;

                const layer = await this.calendar.get();
                this.setActiveLayer(layer);
                this.updateLayers(layer);

                this.loading = false;
            },
            /** @param element {CalendarPublishElement}
             * @param forceReload {Boolean}
             */
            async updateEvent(element, forceReload = true) {
                this.loading = true;
                const result = await this.calendar.update(element);
                EaseEventBus.fire('updateDateTimeForSettings', transformCapitalizedJSONIntoSnakeCase(element));
                if (forceReload) await this.reload(true); // TODO we need this to keep the layer up to date but its inefficient
                this.loading = false;
            },
            /** @param element {CalendarPublishElement} */
            async deleteEvent(element) {
                this.loading = true;
                await this.calendar.delete(element);

                await this.reload(); // TODO we need this to keep the layer up to date but its inefficient
                this.loading = false;
            },
            /** @param element {CalendarPublishElement} */
            async undeleteEvent(element) {
                this.loading = true;
                await this.calendar.undelete(element);

                await this.reload(); // TODO we need this to keep the layer up to date but its inefficient
                this.loading = false;
            },
            /** @param element {CalendarPublishElement} */
            async copyEvent(element) {
                this.loading = true;
                await this.calendar.add(element);
                await this.reload();
                this.loading = false;
            },
            /** @param element {CalendarPublishElement} */
            async unlinkEvent(element) {
                this.loading = true;
                const updatedElement = await this.calendar.unlink(element);
                const index = this.activeLayer.elements.findIndex(el => el.id === element.id);
                this.activeLayer.elements[index].data.state = updatedElement.data.state;
                this.loading = false;
            },
            /** @param element {CalendarPublishElement} */
            async editEvent(element) {
                this.loading = true;
                this.$emit('redirect', element);
            },
            /** @param element {CalendarPublishElement}
             *  @param ignoreValidity {Boolean}
             * */
            async preview(element, ignoreValidity) {
                this.loading = true;
                const resolution = this.activeLayer.width + 'x' + this.activeLayer.height;
                await this.calendar.preview(this.activeLayer.id, [element], resolution, ignoreValidity);
                this.loading = false;
            },
            /** @param info {CalendarDropEvent} */
            handleDropped(info) {
                const {dateStr, allDay, draggedEl} = info;
                const parent = draggedEl.parentElement.parentElement;
                const calendarEl = this.$vnode.elm;

                const dateInfo = getDateInformation(dateStr);

                const properties = {
                    validFrom: dateInfo.ewiDate,
                    validTo: dateInfo.ewiDate
                };

                if (!allDay) {
                    properties.validTimeFrom = `${dateInfo.hour}:${dateInfo.minute}`;
                    const hourPlusOne = addOneHour(dateInfo.hour);
                    properties.validTimeTo = `${hourPlusOne}:${dateInfo.minute}`;
                }

                this.$emit('dropped', parent, this.activeLayer.id, calendarEl, properties);
            },
            /** @param info {CalendarChangeEvent} */
            async handleChanged(info) {
                const {startStr, endStr, allDay, extendedProps} = info.event;
                /** @type {CalendarPublishElement} */
                const element = extendedProps.elementData;

                const dateInfoStart = getDateInformation(startStr);
                element.data.validFrom = dateInfoStart.ewiDate;

                if (!allDay) {
                    element.data.validTimeFrom = `${dateInfoStart.hour}:${dateInfoStart.minute}`;

                    if (endStr) {
                        const dateInfoEnd = getDateInformation(endStr);

                        element.data.validTo = dateInfoEnd.ewiDate;
                        element.data.validTimeTo = `${dateInfoEnd.hour}:${dateInfoEnd.minute}`;
                    }
                } else {
                    element.data.validTimeFrom = undefined;
                    element.data.validTimeTo = undefined;

                    if (endStr) {
                        const dateInfoEnd = getDateInformation(endStr);
                        element.data.validTo = dateInfoEnd.ewiDate;
                    } else {
                        element.data.validTo = dateInfoStart.ewiDate;
                    }

                   element.data.validFrom = dateInfoStart.ewiDate;
                }

                if (element.data.validTimeFrom && !element.data.validTimeTo) {
                    const [hour, minute] = element.data.validTimeFrom.split(':');
                    element.data.validTimeTo = `${addOneHour(hour)}:${minute}`;
                }

                await this.updateEvent(element);
            },
            /**
             * @param element {CalendarPublishElement}
             */
            async handleChangedMenu(element) {
                if (!this.privileges.canApprove()) {
                    toaster.error(this.translations.noApprove);
                    return;
                }

                if (!element.data.approved) {
                    await this.calendar.reject(element);
                }
                await this.updateEvent(element);
            },
            async deletePastElements() {
                const elements = this.activeLayer.elements;
                const date = getRelativeDateString(this.calendarProjectSettings.expiryTime);
                const pastElements = getElementsBeforeDate(elements, date);
                await this.calendar.bulkDelete(pastElements);
                await this.reload();
            },
            async publish() {
                this.loading = true;

                await this.deletePastElements();

                const api = new API('/content/publish/publish');

                const elements = this.activeLayer.elements;

                const data = constructPublishData(this.activeLayer, this.calendar);

                const result = await api.post(data);

                this.loading = false;

                if (result.data?.data?.outdated) {
                    toaster.error(this.translations.outdated);
                    return;
                }

                // TODO Improve publishing

                toaster.success(this.translations.published);

                this.activeLayer.elements = elements
                    .map(element => {
                        const updateElement = element.state !== 'error' && !element.state.includes('deleted');
                        const updatedElement = {...element}
                        if (updateElement) {
                            updatedElement.publishedAt = 'updated';
                            updatedElement.state = 'updating';
                        }
                        return updatedElement;
                    });

                const elementsToBeUpdated = this.activeLayer.elements.filter(elements => elements.state === 'updating');

                for (const element of elementsToBeUpdated) {
                    const response = await this.calendar.update(element);
                    const elementIndex = this.activeLayer.elements.findIndex(el => el.id === element.id);

                    // TODO this is bad because its causing too many re-renders
                    this.activeLayer.elements[elementIndex].state = response.state;
                    this.activeLayer.elements[elementIndex].changeCount = response.changeCount;
                    this.activeLayer.elements[elementIndex].publishedAt = response.publishedAt;
                    this.activeLayer.elements[elementIndex].updatedAt = response.updatedAt;
                }

                await this.reload();
            },
            async handleLayerChange(layerIndex) {
                if (_.isUndefined(layerIndex)) return;

                // value here is the "Nr" column in the layers table
                const layer = this.layers[layerIndex];
                this.calendar.updateLayer(layer.value, layer.id);
                await this.reload();
                this.clearSelection();
            },
            async handleScreenChanged(newScreenId) {
                if (_.isNull(newScreenId) || _.isUndefined(newScreenId)) {
                    return;
                }
                this.calendar.updateScreen(newScreenId);
                await this.reload();
                await this.loadBusinessHours();
                this.clearSelection();
            },
            async reload(forceUIRefresh) {
                await this.loadActiveLayer();

                // TODO move this up into screen-publish
                // Update data in other components for settings to work...
                const activeLayerSnakeCase = transformCapitalizedJSONIntoSnakeCase(this.activeLayer, true);
                EaseEventBus.fire('updateVisibleElements', activeLayerSnakeCase.elements);
                EaseEventBus.fire('updateActiveLayer', activeLayerSnakeCase);
                if (forceUIRefresh) this.forceUIRefresh();
            },
            // This forces a refresh of the settings by unselecting and quickly selecting the current element again
            forceUIRefresh() {
                const oldSelectedElementIndex = this.value;
                this.$emit('input', -1);
                this.$nextTick(() => {
                    this.$emit('input', oldSelectedElementIndex);
                });
            },
            clearSelection() {
                this.$emit('input', -1);
                this.activeEventId = undefined;
            },
            /**
             * @param eventId {Number}
             * @param element {CalendarPublishElement}
             */
            handleClick(eventId, element) {
                this.activeEventId = eventId;
                const elementIndex = this.activeLayer.elements.findIndex(el => el.id === element.id);
                this.$emit('input', elementIndex);
            },
            handleSettingsClicked() {
                this.showSettings = true;
            },
            async handleSettingsClosed(reload = false) {
                this.showSettings = false;
                if (reload) await this.reload();
            },
            /**
             * @param action {CalendarActionItem}
             * @param element {CalendarPublishElement}
             * @returns {Promise<void>}
             */
            async handleActionClick(action, element) {
                switch (action) {
                    case "delete": {
                        await this.deleteEvent(element);
                        break;
                    }
                    case "undelete": {
                        await this.undeleteEvent(element);
                        break;
                    }
                    case "copy": {
                        await this.copyEvent(element);
                        break;
                    }
                    case "unlink": {
                        await this.unlinkEvent(element);
                        break;
                    }
                    case "edit": {
                        await this.editEvent(element);
                        break;
                    }
                    case "preview": {
                        await this.preview(element, true);
                        break;
                    }
                    default: {
                        throw Error('CalendarAction not handled correctly!');
                    }
                }
            },
            getDefaultLayer() {
                const key = getLayerIdLocalStorageKey();
                const lastLayerId = JSON.parse(localStorage.getItem(key));
                if (!lastLayerId) return this.layers[0];

                return this.layers.find(layer => layer.id === lastLayerId);
            },
            getDefaultScreen() {
                const key = getPublishFilterLocalStorageKey();
                const filters = JSON.parse(localStorage.getItem(key));
                if (!filters?.screen) return this.screensData.screens[0].value;

                return filters.screen;
            },
            initializeCalendar() {
                const defaultLayer = this.getDefaultLayer();
                const defaultScreen = this.getDefaultScreen();
                this.calendar = new CalendarClass(defaultLayer.value, defaultScreen);
            },
            async loadBusinessHours() {
                this.loading = true;
                const api = new API(`/administration/client/switch-off-times/${this.calendar.screen}`);
                const result = await api.get();

                this.businessHours = parseBusinessHours(result);
                this.loading = false;
            }
        },
        async mounted() {
            this.initializeCalendar();

            await this.loadBusinessHours();
            await this.reload();

            // listen for updates in settings and update here as well
            EaseEventBus.listen('elementChanged', async element => {
                const elementCamelCased = transformAPIResponseIntoCapitalizedJSON(element, true);

                // This is a safety measure because the changeCount is sometimes not updated correctly and I couldn't figure out where or why.
                const elementInCurrentLayer = this.activeLayer.elements.find(el => element.id === el.id);
                elementCamelCased.changeCount = elementInCurrentLayer.changeCount;

                await this.updateEvent(elementCamelCased);
            });

            // listen for updates in settings and update here as well
            EaseEventBus.listen('changeLayer', layerIndex => {
                this.handleLayerChange(layerIndex);
            });
        },
        beforeDestroy() {
            EaseEventBus.destroy('elementChanged');
            EaseEventBus.destroy('changeLayer');
        }
    }
</script>

<template>
    <div class="d-flex flex-col publish-calendar-container editor-col" style="width: 67%; height: 100%">
        <v-card rounded="lg" id="publish-calendar" elevation="0" style="width: 100%; display: flex; flex-direction: column; justify-content: space-between;">
            <loading :loading="loading" />
            <publish-calendar-actionbar v-if="activeLayer"
                :loading="loading"
                :currentEvents="currentEvents"
                :activeLayer="activeLayer"
                :activeScreenId="calendar.screen"
                :allScreens="screensData.all_screens"
                :layers="layers"
                :dialogText="publishDialogText"
                :dialogAdditionalText="publishDialogExpiryWarningText"
                :publishingDisabled="publishingDisabled"
                @layerChanged="handleLayerChange"
                @screenChanged="handleScreenChanged"
                @reload="reload"
                @publish="publish"
                @approveAll="handleApproveAll"
                @disapproveAll="handleDisapproveAll"
            />
            <div class="mx-4">
                <calendar v-if="activeLayer"
                    v-bind="$attrs"
                    :events="currentEvents"
                    :activeEventId="activeEventId"
                    :privileges="privileges"
                    :businessHours="businessHours"
                    @dropped="handleDropped"
                    @changed="handleChanged"
                    @changed:menu="handleChangedMenu"
                    @clicked="handleClick"
                    @clickedAction="handleActionClick"
                    @clickedSettings="handleSettingsClicked"
                />
            </div>
            <publish-footer v-if="activeLayer?.elements"
                :deletedElements="deletedElements"
                :elements="activeLayer.elements"
                showDeleted
                hideCheckbox
            />
            <transmission/>
            <calendar-settings-dialog v-if="showSettings"
                :showDialog="showSettings"
                :defaultSettings="calendarProjectSettings"
                @close="handleSettingsClosed"
            />
        </v-card>
    </div>
</template>
